'use client'

/* ─────────────────────────────────────────────────────────────────────────────
   FABLE COMPONENT KIT (DESIGN_SYSTEM.md) — the only building blocks pages use.
   PageHero · Section · StatChip · KitCard (default/glass/dark) · CampaignKitCard
   · MasjidKitCard · FeedPostCard · EmptyState
───────────────────────────────────────────────────────────────────────────── */

import React, { useEffect, useRef } from 'react'
import Link from 'next/link'
import { MapPin, BadgeCheck, Users, Clock, ArrowRight, Sparkles } from 'lucide-react'
import { HeroBackdrop, MasjidPlaceholder, CampaignPlaceholder, CAMPAIGN_CATEGORIES, type CampaignCategory, type HeroRole } from '../brand/assets'
import { masjidPlaceholder } from '../../lib/masjid-placeholder'
import { formatAddress } from '../../lib/format-address'
import { sourceBadgeLabel } from '../../lib/provenance'

/* ── Count-up (easeOutCubic 1400ms en-IN, guaranteed finish) ── */
export function KitCount({ n, dec = 0 }: { n: number; dec?: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const fmt = (v: number) => (dec ? v.toFixed(dec) : Math.round(v).toLocaleString('en-IN'))
    let safety: ReturnType<typeof setTimeout> | undefined
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { el.textContent = fmt(n); io.unobserve(el); return }
        const t0 = performance.now()
        const step = (t: number) => {
          const p = Math.min(1, (t - t0) / 1400)
          el.textContent = fmt(n * (1 - Math.pow(1 - p, 3)))
          if (p < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
        safety = setTimeout(() => { el.textContent = fmt(n) }, 1700)
        io.unobserve(el)
      })
    }, { threshold: 0.5 })
    io.observe(el)
    return () => { io.disconnect(); if (safety) clearTimeout(safety) }
  }, [n, dec])
  const final = dec ? n.toFixed(dec) : Math.round(n).toLocaleString('en-IN')
  /* CLS: reserve the final width so the count-up animates in place */
  return <span ref={ref} style={{ display: 'inline-block', minWidth: `${final.length}ch`, fontVariantNumeric: 'tabular-nums' }}>0</span>
}

/* ── PageHero — the dark navy band every page opens with ── */
export function PageHero({
  eyebrow, title, subtitle, actions, widget, stats, compact, art = 'dome', motif,
}: {
  eyebrow: string
  title: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  widget?: React.ReactNode
  stats?: Array<{ value: number | string; label: string; prefix?: string; suffix?: string; dec?: number }>
  compact?: boolean
  /* transparent hero artwork from public/hero-art (D2) */
  art?: 'dome' | 'skyline' | 'giving' | 'signal' | 'scales' | 'book' | 'work' | 'calendar'
  /* brand asset system per-role motif (DESIGN_SYSTEM.md SS12) - replaces art when set */
  motif?: HeroRole
}) {
  return (
    <header className={'kit-hero' + (compact ? ' compact' : '') + (widget ? ' has-widget' : '')}>
      <div className="kh-bg" style={{ ['--kh-art' as any]: motif ? 'none' : `url('/hero-art/${art}.svg')` }}>
        {motif && <div className="kh-motif"><HeroBackdrop role={motif} height="100%" /></div>}
        <div className="kh-pattern" /><div className="kh-noise" />
      </div>
      <div className="kh-wrap">
        <div className="kh-main">
          <span className="kh-eyebrow"><span className="fable-live-dot" /> {eyebrow}</span>
          <h1>{title}</h1>
          {subtitle && <p className="kh-sub">{subtitle}</p>}
          {actions && <div className="kh-actions">{actions}</div>}
          {stats && stats.length > 0 && (
            <div className="kh-stats">
              {stats.map((s) => (
                <StatChip key={s.label} value={s.value} label={s.label} prefix={s.prefix} suffix={s.suffix} dec={s.dec} onDark />
              ))}
            </div>
          )}
        </div>
        {widget && <div className="kh-widget">{widget}</div>}
      </div>

      <style jsx>{`
        /* g: fixed proportions — 280-340px desktop band, 220px mobile, never
           a third of the viewport on internal pages */
        .kit-hero{position:relative;display:flex;flex-direction:column;justify-content:center;
          min-height:300px;max-height:max(340px,30vh);padding:40px 0 52px;z-index:0;overflow:hidden}
        .kit-hero.compact{min-height:280px;padding:32px 0 48px}
        /* e: the band is CONTAINED (bottom:0, no mask spill) and ends with a
           short 140px navy->cream linear strip INSIDE its bottom edge — the
           one shared transition; fog/radial overlays are banned */
        /* SOLID brand navy — no gradient, no fade-to-cream: a clean, definite edge
           where the hero meets the page. Subtle dot-grid + noise stay for texture. */
        .kh-bg{position:absolute;left:0;right:0;top:0;bottom:0;z-index:-1;pointer-events:none;background:#0B132B}
        /* f: per-role motif anchored right, contained, masked away from the
           headline column; fades out below 760px instead of cropping */
        .kh-motif{position:absolute;top:0;bottom:0;right:0;width:min(720px,52%);overflow:hidden;
          -webkit-mask-image:linear-gradient(90deg,transparent 0%,#000 22%);
          mask-image:linear-gradient(90deg,transparent 0%,#000 22%)}
        .kh-motif :global(svg){width:100%;height:100%}
        @media(max-width:760px){.kh-motif{opacity:0}}
        .kh-pattern{position:absolute;inset:0;opacity:.04;background-image:radial-gradient(rgba(255,255,255,.85) 1px,transparent 1.5px);background-size:26px 26px}
        .kh-noise{position:absolute;inset:0;opacity:.05;mix-blend-mode:overlay;background-image:url('/homepage-assets/noise.svg')}
        .kh-wrap{max-width:1280px;margin:0 auto;padding:0 26px}
        .has-widget .kh-wrap{display:grid;grid-template-columns:1.05fr .95fr;gap:40px;align-items:center}
        .kh-eyebrow{display:inline-flex;align-items:center;gap:9px;font-family:var(--f-mono);font-size:10.5px;letter-spacing:.18em;color:#14B8A6;border:1px solid rgba(20,184,166,.35);border-radius:99px;padding:5px 13px;background:rgba(20,184,166,.08);margin-bottom:16px;text-transform:uppercase}
        h1{font-family:var(--f-display);font-size:clamp(27px,3.2vw,40px);line-height:1.14;font-weight:700;color:#F6F4EE;letter-spacing:-.01em;max-width:760px}
        h1 :global(.em){color:#34D399}
        h1 :global(.gold){color:#E6C963}
        .kh-sub{margin:12px 0 0;color:rgba(246,244,238,.75);font-size:15px;max-width:620px;line-height:1.55}
        .kh-actions{margin-top:20px;display:flex;gap:10px;flex-wrap:wrap;align-items:center}
        .kh-stats{display:flex;gap:12px;flex-wrap:wrap;margin-top:24px}
        @media(max-width:980px){.has-widget .kh-wrap{grid-template-columns:1fr;gap:26px}}
        @media(max-width:640px){.kit-hero,.kit-hero.compact{min-height:220px;max-height:none;padding:28px 0 40px}.kh-stats{width:100%}}
        @media(prefers-reduced-motion:reduce){.kit-hero :global(*){animation:none!important;transition:none!important}}
      `}</style>
    </header>
  )
}

/* ── Section — standard vertical rhythm ── */
export function Section({
  eyebrow, title, link, children, tone = 'light', id,
}: {
  eyebrow?: string
  title?: React.ReactNode
  link?: { label: string; href: string }
  children: React.ReactNode
  tone?: 'light' | 'dark'
  id?: string
}) {
  return (
    <section id={id} className={'kit-section' + (tone === 'dark' ? ' dark' : '')}>
      <div className="ks-wrap">
        {(eyebrow || title || link) && (
          <div className="ks-head">
            <div>
              {eyebrow && <div className="ks-eyebrow">{eyebrow}</div>}
              {title && <h2>{title}</h2>}
            </div>
            {link && <Link className="ks-link" href={link.href}>{link.label}</Link>}
          </div>
        )}
        {children}
      </div>
      <style jsx>{`
        .kit-section{padding:34px 0}
        .kit-section.dark{background:linear-gradient(160deg,#101A38,#0B132B);padding:40px 0}
        .ks-wrap{max-width:1280px;margin:0 auto;padding:0 26px}
        .ks-head{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:22px;gap:20px}
        .ks-eyebrow{font-family:var(--f-mono);font-size:10px;letter-spacing:.2em;color:var(--f-green);margin-bottom:8px;text-transform:uppercase}
        .dark .ks-eyebrow{color:#14B8A6}
        h2{font-family:var(--f-display);font-size:24px;font-weight:700;color:var(--f-navy);letter-spacing:-.01em}
        .dark h2{color:#fff}
        .kit-section :global(.ks-link){font-size:12.5px;font-weight:600;color:var(--f-green);text-decoration:none;white-space:nowrap}
        .dark :global(.ks-link){color:#E6C963}
      `}</style>
    </section>
  )
}

/* ── StatChip — numbers NEVER wrap ── */
export function StatChip({
  value, label, prefix, suffix, dec = 0, onDark = false,
}: {
  value: number | string
  label: string
  prefix?: string
  suffix?: string
  dec?: number
  onDark?: boolean
}) {
  return (
    <div className={'kit-stat' + (onDark ? ' on-dark' : '')}>
      <div className="kst-n">
        {prefix}{typeof value === 'number' ? <KitCount n={value} dec={dec} /> : value}
        {suffix ? <i> {suffix}</i> : null}
      </div>
      <div className="kst-k">{label}</div>
      <style jsx>{`
        .kit-stat{background:#fff;border:1px solid var(--f-line);border-radius:13px;padding:11px 17px;min-width:104px;box-shadow:var(--f-shadow)}
        .kit-stat.on-dark{background:rgba(255,255,255,.06);border-color:rgba(255,255,255,.12);box-shadow:none;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
        .kst-n{font-family:var(--f-display);font-weight:700;font-size:21px;color:var(--f-navy);
          white-space:nowrap;font-variant-numeric:tabular-nums;line-height:1.1}
        .on-dark .kst-n{color:#fff}
        .kst-n i{font-style:normal;color:var(--f-gold-deep);font-size:15px}
        .on-dark .kst-n i{color:#E6C963}
        .kst-k{font-family:var(--f-mono);font-size:8.5px;letter-spacing:.12em;color:var(--f-slate-dim);margin-top:3px;text-transform:uppercase;white-space:nowrap}
        .on-dark .kst-k{color:#8A94A8}
      `}</style>
    </div>
  )
}

/* ── KitCard — default / glass / dark ── */
export function KitCard({
  variant = 'default', className = '', children, interactive = false,
}: {
  variant?: 'default' | 'glass' | 'dark'
  className?: string
  children: React.ReactNode
  interactive?: boolean
}) {
  return (
    <div className={`kit-card kc-${variant}${interactive ? ' kc-hover' : ''} ${className}`}>
      {children}
      <style jsx>{`
        .kit-card{border-radius:16px;padding:17px}
        .kc-default{background:#fff;border:1px solid var(--f-line);box-shadow:var(--f-shadow)}
        .kc-glass{backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);background:rgba(255,255,255,.9);border:1px solid rgba(255,255,255,.65);box-shadow:0 16px 44px rgba(11,19,43,.22)}
        .kc-dark{background:rgba(13,21,44,.94);border:1px solid rgba(255,255,255,.12);color:#F6F4EE;backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px)}
        .kc-hover{transition:transform .25s,box-shadow .25s}
        .kc-hover:hover{transform:translateY(-4px);box-shadow:var(--f-shadow-lg)}
      `}</style>
    </div>
  )
}

/* ── EmptyState — never a blank void ── */
export function EmptyState({
  icon, title, body, cta,
}: {
  icon?: React.ReactNode
  title: string
  body: string
  cta?: { label: string; href: string }
}) {
  return (
    <div className="kit-empty">
      {icon && <div className="ke-icon">{icon}</div>}
      <h3>{title}</h3>
      <p>{body}</p>
      {cta && <Link href={cta.href} className="ke-cta">{cta.label} →</Link>}
      <style jsx>{`
        .kit-empty{text-align:center;padding:34px 24px;background:linear-gradient(150deg,#FBFAF6,#fff);border:1px dashed var(--f-line-strong);border-radius:16px}
        .ke-icon{width:44px;height:44px;border-radius:13px;background:rgba(14,124,97,.08);color:var(--f-green);display:grid;place-items:center;margin:0 auto 12px}
        h3{font-family:var(--f-display);font-size:15.5px;font-weight:600;color:var(--f-navy)}
        p{font-size:12.5px;color:var(--f-slate);margin:6px auto 0;max-width:380px;line-height:1.5}
        .kit-empty :global(.ke-cta){display:inline-block;margin-top:14px;background:var(--f-green);color:#fff;font-size:12.5px;font-weight:600;border-radius:9px;padding:9px 16px;text-decoration:none}
      `}</style>
    </div>
  )
}

/* ── CampaignKitCard ── */
export interface CampaignCardData {
  id: string
  title: string
  masjid?: string
  city?: string
  category?: string
  raised: number
  goal: number
  donors?: number
  daysLeft?: number
  coverPhoto?: string | null
  verified?: boolean
  featured?: boolean
}

/* brand asset system (3d, approved): map free-text campaign categories onto
   the 8 brand cause categories */
export function brandCampaignCategory(category?: string): CampaignCategory {
  const c = String(category || '').toLowerCase()
  for (const known of CAMPAIGN_CATEGORIES) if (c.includes(known)) return known
  if (/hall|build|construct|minaret/.test(c)) return 'construction'
  if (/renovat|repair|ac |solar|infra/.test(c)) return 'renovation'
  if (/madrassa|school|library|study|quran/.test(c)) return 'education'
  if (/water|well|wudu/.test(c)) return 'water'
  if (/medical|health|clinic/.test(c)) return 'medical'
  if (/food|iftar|ration/.test(c)) return 'food'
  if (/waqf|land|protect/.test(c)) return 'waqf'
  return 'general'
}

const lakh = (v: number) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${v.toLocaleString('en-IN')}`)

export function CampaignKitCard({ c }: { c: CampaignCardData }) {
  const pct = Math.min(100, Math.round((c.raised / Math.max(1, c.goal)) * 100))
  return (
    <Link href={`/campaign/${c.id}`} className={'kit-camp' + (c.featured ? ' featured' : '')}>
      <div className="kcp-cover">
        {c.coverPhoto && <img className="kcp-cover-img" src={c.coverPhoto} alt="" fetchPriority={c.featured ? 'high' : undefined} loading={c.featured ? 'eager' : 'lazy'} decoding="async" />}
        {!c.coverPhoto && (
          <span className="kcp-brand-ph" aria-hidden>
            <CampaignPlaceholder category={brandCampaignCategory(c.category)} ratio="card" />
          </span>
        )}
        {c.category && <span className="kcp-tag">{c.category.toUpperCase()}</span>}
        {c.verified && <span className="kcp-verified"><BadgeCheck size={11} strokeWidth={1.5} /> VERIFIED</span>}
      </div>
      <div className="kcp-body">
        <h3>{c.title}</h3>
        {(c.masjid || c.city) && <div className="kcp-masjid">{formatAddress(c.masjid, c.city)}</div>}
        {/* Progress bar + % only once real money is in. At ₹0 show the target as plain text —
            no bar, no "0%" (which would imply a stalled campaign rather than a fresh one). */}
        {c.raised > 0 ? (
          <>
            <div className="kcp-bar"><i style={{ width: `${pct}%` }} /></div>
            <div className="kcp-nums"><span><b>{lakh(c.raised)}</b> of {lakh(c.goal)}</span><span>{pct}%</span></div>
          </>
        ) : (
          <div className="kcp-nums"><span>Target: <b>{lakh(c.goal)}</b></span></div>
        )}
        <div className="kcp-meta">
          {c.donors ? <span><Users size={11} strokeWidth={1.5} /> <b>{c.donors}</b> donors</span> : <span />}
          {typeof c.daysLeft === 'number' ? <span><Clock size={11} strokeWidth={1.5} /> <b>{c.daysLeft}</b> days left</span> : null}
        </div>
      </div>
      <style jsx>{`
        .kit-camp{display:block;border:1px solid var(--f-line);border-radius:16px;overflow:hidden;background:#fff;box-shadow:var(--f-shadow);transition:transform .25s,box-shadow .25s;text-decoration:none;color:inherit}
        .kit-camp:hover{transform:translateY(-4px);box-shadow:var(--f-shadow-lg)}
        .kit-camp.featured{background:linear-gradient(150deg,#FDF7E3,#fff);border-color:rgba(212,175,55,.4)}
        .kcp-cover{height:118px;position:relative;display:flex;align-items:flex-start;justify-content:space-between;padding:10px;overflow:hidden}
        .kcp-cover :global(.kcp-cover-img){position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0}
        .kcp-brand-ph{position:absolute;inset:0;z-index:0;pointer-events:none}
        .kcp-brand-ph :global(svg){width:100%;height:100%;object-fit:cover}
        .kcp-cover > :global(*:not(.kcp-brand-ph)){position:relative;z-index:1}
        .kcp-tag{position:relative;backdrop-filter:blur(8px);background:rgba(255,255,255,.92);border-radius:99px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.08em;color:var(--f-green-deep);padding:4px 9px;font-weight:600}
        .kcp-verified{position:relative;display:inline-flex;align-items:center;gap:4px;backdrop-filter:blur(8px);background:rgba(20,184,166,.92);border-radius:99px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.08em;color:#06302a;padding:4px 9px;font-weight:600}
        .kcp-body{padding:14px 15px 15px}
        h3{font-family:var(--f-display);font-weight:600;font-size:14px;color:var(--f-navy);line-height:1.35}
        .kcp-masjid{font-size:11px;color:var(--f-slate);margin-top:2px}
        .kcp-bar{height:6px;border-radius:99px;background:rgba(11,19,43,.07);overflow:hidden;margin-top:12px}
        .kcp-bar i{display:block;height:100%;border-radius:99px;background:linear-gradient(90deg,var(--f-green),var(--f-teal))}
        .featured .kcp-bar i{background:linear-gradient(90deg,var(--f-gold),var(--f-gold-soft))}
        .kcp-nums{display:flex;justify-content:space-between;margin-top:7px;font-family:var(--f-mono);font-size:9.5px;color:var(--f-slate);white-space:nowrap}
        .kcp-nums b{color:var(--f-navy);font-weight:600}
        .kcp-meta{display:flex;justify-content:space-between;margin-top:8px;font-size:10.5px;color:var(--f-slate)}
        .kcp-meta span{display:inline-flex;align-items:center;gap:4px}
        .kcp-meta b{color:var(--f-navy)}
      `}</style>
    </Link>
  )
}

/* ── MasjidKitCard ── */
export interface MasjidCardData {
  id?: string | null
  slug: string
  name: string
  area?: string | null
  city?: string | null
  verified?: boolean | null
  coverPhoto?: string | null
  photoReady?: boolean | null
  capacity?: number | null
  facilities?: string[] | null
  importSource?: string | null
}

export function MasjidKitCard({ m, nextPrayer }: { m: MasjidCardData; nextPrayer?: { name: string; time: string } | null }) {
  /* Cover priority: uploaded coverPhoto → Google photo (when photo_ready) → branded
     placeholder. Layered backgrounds are fail-soft: a 404 on the Google photo route
     paints nothing and the placeholder SVG layer shows through (no broken-image icon). */
  const coverUrl = m.coverPhoto || (m.photoReady && m.id ? `/api/masjids/${m.id}/photo` : null)
  return (
    <div className="kit-masjid">
      <Link href={`/masjids/${m.slug}`} className="km-link">
        <div className="km-cover" style={coverUrl ? { backgroundImage: `url(${coverUrl}), url(${masjidPlaceholder(m.slug)})` } : undefined}>
          {!coverUrl && (
            <span className="km-brand-ph" aria-hidden>
              <MasjidPlaceholder slug={m.slug} ratio="card" />
            </span>
          )}
          <span className={'km-badge' + (m.verified ? '' : ' listed')}>
            {m.verified ? <><BadgeCheck size={11} strokeWidth={1.5} /> VERIFIED</> : sourceBadgeLabel(m.importSource).short}
          </span>
        </div>
        <div className="km-body">
          <h3>{m.name}</h3>
          <div className="km-loc"><MapPin size={11} strokeWidth={1.5} /> {formatAddress(m.area, m.city) || 'India'}</div>
          <div className="km-meta">
            <div><div className="v">{nextPrayer ? `${nextPrayer.name} ${nextPrayer.time.replace(/\s(AM|PM)/, '')}` : '—'}</div><div className="k">NEXT SALAH</div></div>
            <div><div className="v">{m.capacity ? m.capacity.toLocaleString('en-IN') : '—'}</div><div className="k">CAPACITY</div></div>
            <div><div className="v">{m.facilities?.length || 0}</div><div className="k">FACILITIES</div></div>
          </div>
        </div>
      </Link>
      {!m.verified && (
        <Link href="/masjid/claim" className="km-claim">On this committee? Claim this masjid →</Link>
      )}
      <style jsx>{`
        .kit-masjid{display:flex;flex-direction:column;border:1px solid var(--f-line);border-radius:16px;overflow:hidden;background:#fff;box-shadow:var(--f-shadow);transition:transform .25s,box-shadow .25s}
        .kit-masjid:hover{transform:translateY(-4px);box-shadow:var(--f-shadow-lg)}
        .kit-masjid :global(.km-link){display:block;flex:1;text-decoration:none;color:inherit}
        .kit-masjid :global(.km-claim){display:block;padding:9px 15px;background:rgba(14,124,97,.06);border-top:1px solid var(--f-line);font-size:11.5px;font-weight:600;color:var(--f-green-deep);text-decoration:none;transition:background .2s}
        .kit-masjid :global(.km-claim:hover){background:rgba(14,124,97,.12)}
        .km-cover{height:126px;background-size:cover;background-position:center;position:relative;padding:10px;overflow:hidden}
        .km-brand-ph{position:absolute;inset:0;z-index:0;pointer-events:none}
        .km-brand-ph :global(svg){width:100%;height:100%}
        .km-cover > :global(*:not(.km-brand-ph)){position:relative;z-index:1}
        .km-badge{display:inline-flex;align-items:center;gap:4px;backdrop-filter:blur(8px);background:rgba(255,255,255,.92);border-radius:99px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.08em;color:var(--f-green-deep);padding:4px 9px;font-weight:600}
        .km-badge.listed{color:var(--f-slate)}
        .km-body{padding:14px 15px 15px}
        h3{font-family:var(--f-display);font-weight:600;font-size:14.5px;color:var(--f-navy);line-height:1.3}
        .km-loc{display:flex;align-items:center;gap:4px;font-size:11px;color:var(--f-slate);margin-top:3px}
        .km-meta{display:flex;gap:12px;margin-top:12px;padding-top:12px;border-top:1px solid var(--f-line)}
        .km-meta > div{flex:1}
        .km-meta .v{font-family:var(--f-mono);font-size:11.5px;color:var(--f-navy);font-weight:500;white-space:nowrap}
        .km-meta .k{font-family:var(--f-mono);font-size:8px;letter-spacing:.08em;color:var(--f-slate-dim);margin-top:1px}
      `}</style>
    </div>
  )
}

/* ── FeedPostCard ── */
export function FeedPostCard({
  type, title, body, author, time, href,
}: {
  type: string
  title: string
  body?: string
  author?: string
  time?: string
  href?: string
}) {
  const inner = (
    <>
      <div className="kf-head">
        <span className="kf-type">{type.toUpperCase()}</span>
        {time && <span className="kf-time">{time}</span>}
      </div>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {author && <div className="kf-author"><BadgeCheck size={12} strokeWidth={1.5} /> {author}</div>}
      <style jsx>{`
        .kf-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
        .kf-type{font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;color:var(--f-green);background:rgba(14,124,97,.08);border-radius:99px;padding:3px 9px;font-weight:600}
        .kf-time{font-size:10.5px;color:var(--f-slate-dim)}
        h3{font-family:var(--f-display);font-weight:600;font-size:14px;color:var(--f-navy);line-height:1.35}
        p{font-size:12px;color:var(--f-slate);margin-top:5px;line-height:1.5}
        .kf-author{display:flex;align-items:center;gap:5px;font-size:11px;color:var(--f-green-deep);margin-top:10px;font-weight:500}
      `}</style>
    </>
  )
  const cls = 'kit-card kc-default kc-hover'
  return href ? <Link href={href} className={cls} style={{ display: 'block', textDecoration: 'none', color: 'inherit', borderRadius: 16, padding: 17 }}>{inner}</Link> : <KitCard interactive>{inner}</KitCard>
}

/* ── AIBadge — small mono chip marking AI-generated content (Phase 3) ── */
export function AIBadge({ label = 'AI' }: { label?: string }) {
  return (
    <span className="ai-badge" title="Generated by AI from real listing data — may be imperfect.">
      <Sparkles size={9} strokeWidth={1.5} /> {label}
      <style jsx>{`
        .ai-badge{display:inline-flex;align-items:center;gap:4px;font-family:var(--f-mono);font-size:8.5px;letter-spacing:.1em;color:#7C5CBF;background:rgba(124,92,191,.1);border:1px solid rgba(124,92,191,.22);border-radius:99px;padding:3px 8px;font-weight:600;vertical-align:middle}
      `}</style>
    </span>
  )
}

/* ── AIShimmer — loading treatment for in-flight AI generations ── */
export function AIShimmer({ lines = 3 }: { lines?: number }) {
  return (
    <div className="ai-shimmer" aria-busy="true" aria-label="Generating">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="ai-shimmer-line" style={{ width: `${100 - i * 14}%` }} />
      ))}
      <style jsx>{`
        .ai-shimmer{display:grid;gap:8px;padding:4px 0}
        .ai-shimmer-line{height:11px;border-radius:6px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:aish 1.4s linear infinite}
        @keyframes aish{from{background-position:200% 0}to{background-position:-200% 0}}
        @media(prefers-reduced-motion:reduce){.ai-shimmer-line{animation:none}}
      `}</style>
    </div>
  )
}

export { ArrowRight as KitArrow }
